import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowUUpLeft, Plus } from "@phosphor-icons/react";

const COLORS = { REQUESTED: "bg-stone-100 text-stone-600", APPROVED: "bg-blue-100 text-blue-700", PROCESSING: "bg-amber-100 text-amber-700", PROCESSED: "bg-green-100 text-green-700", FAILED: "bg-red-100 text-red-700", REJECTED: "bg-stone-200 text-stone-500" };

export default function AdminRefunds() {
  const [data, setData] = useState(null);
  const [adj, setAdj] = useState([]);
  const [createDlg, setCreateDlg] = useState(false);
  const [adjDlg, setAdjDlg] = useState(false);
  const [rf, setRf] = useState({ booking_id: "", amount: "", reason: "" });
  const [af, setAf] = useState({ booking_id: "", amount: "", type: "CREDIT", reason: "", note: "" });

  const load = () => {
    api.get("/admin/finance/refunds").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load refunds"));
    api.get("/admin/finance/adjustments").then((r) => setAdj(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const approve = (r) => api.patch(`/admin/finance/refunds/${r.id}/approve`, { approved_amount: r.requested_amount }).then(() => { toast.success("Approved"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  const process = (r) => api.post(`/admin/finance/refunds/${r.id}/process`, {}).then(() => { toast.success("Refund processed"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  const reject = (r) => api.patch(`/admin/finance/refunds/${r.id}/reject`, { reason: "Rejected by admin" }).then(() => { toast.success("Rejected"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));

  const createRefund = () => api.post("/admin/finance/refunds", { booking_id: rf.booking_id, amount: parseFloat(rf.amount), reason: rf.reason })
    .then(() => { toast.success("Refund created"); setCreateDlg(false); setRf({ booking_id: "", amount: "", reason: "" }); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  const createAdj = () => api.post("/admin/finance/adjustments", { ...af, amount: parseFloat(af.amount) })
    .then(() => { toast.success("Adjustment recorded"); setAdjDlg(false); setAf({ booking_id: "", amount: "", type: "CREDIT", reason: "", note: "" }); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));

  if (!data) return <DashboardLayout nav={adminNav} title="Admin"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex items-center gap-2">
        <ArrowUUpLeft size={26} className="text-primary" />
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Refunds</h1>
        <Button data-testid="refund-create-btn" size="sm" className="ml-auto gap-1.5 rounded-full" onClick={() => setCreateDlg(true)}><Plus size={16} /> New refund</Button>
      </div>
      <p className="mt-1 text-sm text-stone-500">Refund amounts follow the cancellation policy. Partial refunds supported. Original payment history is never altered.</p>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        <table className="w-full text-sm" data-testid="refunds-table">
          <thead className="bg-stone-50 text-left text-xs text-stone-500"><tr>
            <th className="p-3">Ref</th><th className="p-3">Booking</th><th className="p-3">Customer</th>
            <th className="p-3 text-right">Paid</th><th className="p-3 text-right">Requested</th><th className="p-3 text-right">Approved</th>
            <th className="p-3 text-right">Non-refundable</th><th className="p-3">Status</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>
            {data.refunds.length === 0 ? <tr><td colSpan={9} className="p-6 text-center text-stone-400">No refunds yet.</td></tr> :
              data.refunds.map((r) => (
                <tr key={r.id} className="border-t border-stone-100" data-testid={`refund-row-${r.id}`}>
                  <td className="p-3 font-medium text-stone-800">{r.refund_ref}</td>
                  <td className="p-3 text-stone-500">{r.booking_ref}</td>
                  <td className="p-3 text-stone-600">{r.customer_name}</td>
                  <td className="p-3 text-right">{formatINR(r.original_amount)}</td>
                  <td className="p-3 text-right">{formatINR(r.requested_amount)}</td>
                  <td className="p-3 text-right">{formatINR(r.approved_amount)}</td>
                  <td className="p-3 text-right text-red-600">{formatINR(r.non_refundable)}</td>
                  <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${COLORS[r.status] || "bg-stone-100"}`}>{r.status}</span></td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {r.status === "REQUESTED" && <button data-testid={`refund-approve-${r.id}`} onClick={() => approve(r)} className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white">Approve</button>}
                      {r.status === "REQUESTED" && <button data-testid={`refund-reject-${r.id}`} onClick={() => reject(r)} className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700">Reject</button>}
                      {["APPROVED", "FAILED"].includes(r.status) && <button data-testid={`refund-process-${r.id}`} onClick={() => process(r)} className="rounded-md bg-green-600 px-2 py-1 text-xs text-white">Process</button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Manual adjustments */}
      <div className="mt-8 flex items-center gap-2">
        <h2 className="font-heading text-lg font-semibold text-stone-900">Manual Adjustments</h2>
        <Button data-testid="adj-create-btn" size="sm" variant="outline" className="ml-auto gap-1.5 rounded-full" onClick={() => setAdjDlg(true)}><Plus size={16} /> New adjustment</Button>
      </div>
      <div className="mt-3 space-y-2" data-testid="adjustments-list">
        {adj.length === 0 ? <p className="rounded-xl border border-dashed border-stone-200 p-4 text-sm text-stone-400">No adjustments recorded.</p> :
          adj.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm">
              <div><span className="font-medium text-stone-800">{a.adj_ref}</span> · {a.booking_ref} · <span className={a.type === "CREDIT" ? "text-green-600" : "text-red-600"}>{a.type} {formatINR(a.amount)}</span></div>
              <span className="text-xs text-stone-400">{a.reason}</span>
            </div>
          ))}
      </div>

      <Dialog open={createDlg} onOpenChange={setCreateDlg}>
        <DialogContent data-testid="refund-create-dialog">
          <DialogHeader><DialogTitle>Create refund</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-stone-500">Booking ID</label><Input data-testid="rf-booking" value={rf.booking_id} onChange={(e) => setRf({ ...rf, booking_id: e.target.value })} /></div>
            <div><label className="text-xs text-stone-500">Refund amount (₹)</label><Input data-testid="rf-amount" type="number" value={rf.amount} onChange={(e) => setRf({ ...rf, amount: e.target.value })} /></div>
            <div><label className="text-xs text-stone-500">Reason</label><Input data-testid="rf-reason" value={rf.reason} onChange={(e) => setRf({ ...rf, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="rf-submit" onClick={createRefund}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adjDlg} onOpenChange={setAdjDlg}>
        <DialogContent data-testid="adj-create-dialog">
          <DialogHeader><DialogTitle>Manual financial adjustment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-stone-500">Booking ID</label><Input data-testid="adj-booking" value={af.booking_id} onChange={(e) => setAf({ ...af, booking_id: e.target.value })} /></div>
            <div><label className="text-xs text-stone-500">Amount (₹)</label><Input data-testid="adj-amount" type="number" value={af.amount} onChange={(e) => setAf({ ...af, amount: e.target.value })} /></div>
            <div><label className="text-xs text-stone-500">Type</label>
              <select data-testid="adj-type" value={af.type} onChange={(e) => setAf({ ...af, type: e.target.value })} className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"><option>CREDIT</option><option>DEBIT</option></select></div>
            <div><label className="text-xs text-stone-500">Reason</label><Input data-testid="adj-reason" value={af.reason} onChange={(e) => setAf({ ...af, reason: e.target.value })} /></div>
            <div><label className="text-xs text-stone-500">Note (optional)</label><Input data-testid="adj-note" value={af.note} onChange={(e) => setAf({ ...af, note: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="adj-submit" onClick={createAdj}>Record adjustment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
